import type { ReactElement } from 'react';
import { Drivers } from '../components/Drivers';
import type { Driver } from '../../../../../types';
import { getSelectedDriverRtk } from '../../sidebar/sidebarSlice';
import { getDriversRtk, getSelectedDriverIdRtk, hasFetchedDriversOnceRtk } from '../../../appSlice';
import { isFetchTagsInProgressRtk } from '../../../../Drivers.selectors';
import { getUserAccount } from '../../../../../configuration/login/loginSlice';
import { isEmpty } from 'ramda';
import { useAppSelector } from '../../../../../configuration/setup/hooks';
import { useGetDriversQuery } from '../../../services/driversApi';
import { useGetUsersQuery } from '../../../services/usersApi';
import { useGetTagsQuery } from '../../../services/tagsApi';
import { config } from '../../../../../config';

export interface DriversPropertiesFromState {
    selectedDriver: Driver | null;
    selectedDriverId: string | null;
    header: ReactElement;
    isTableLoading: boolean;
    accountId: string | undefined;
    hasDrivers: boolean;
    hasFetchedDriversOnce: boolean;
}

interface DriversContainerProps {
    header: ReactElement;
}

export const DriversContainer = ({ header }: DriversContainerProps) => {
    const accountId = useAppSelector(getUserAccount);
    const canQueryDrivers = Boolean(config.backend.DRIVERADMIN_SERVICE);
    const canQueryUsers = Boolean(config.backend.USERADMIN_SERVICE);
    const canQueryTags = Boolean(config.backend.TAGS_SERVICE && accountId);

    useGetDriversQuery(undefined, {
        skip: !canQueryDrivers,
    });
    const usersQuery = useGetUsersQuery(undefined, {
        skip: !canQueryUsers,
    });
    const tagsQuery = useGetTagsQuery(accountId ?? '', {
        skip: !canQueryTags,
    });
    const selectedDriver = useAppSelector(getSelectedDriverRtk);
    const selectedDriverId = useAppSelector(getSelectedDriverIdRtk);
    const hasFetchedDriversOnce = useAppSelector(hasFetchedDriversOnceRtk);
    const isFetchTagsInProgress = useAppSelector(isFetchTagsInProgressRtk);
    const hasDrivers = !isEmpty(useAppSelector(getDriversRtk));
    const isUsersInitialLoadPending = canQueryUsers && !usersQuery.isSuccess && !usersQuery.isError;
    const isTagsInitialLoadPending = canQueryTags && !tagsQuery.isSuccess && !tagsQuery.isError;

    const props: DriversPropertiesFromState = {
        selectedDriver,
        selectedDriverId,
        header,
        isTableLoading: !hasFetchedDriversOnce || isUsersInitialLoadPending || isTagsInitialLoadPending || isFetchTagsInProgress,
        accountId,
        hasDrivers,
        hasFetchedDriversOnce,
    };

    return <Drivers {...props} />;
};
